import {
  AdjacentShopperDetails,
  OrderItems,
  UpcStatus,
} from '../../../batchDetails/types';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import React, {useCallback, useMemo, useState} from 'react';
import {isNSC2, isPlu} from '@buncha/batchDetails/utils/ScannedAddItem';

import {Box} from '../../../components/core/view';
import {CustomFlatList} from '../../../components/core/list';
import {Divider} from 'native-base';
import DropItemModalBody from '../../../itemDetails/components/ItemDetail/DropItemModalBody/DropItemModalBody';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {ItemScannerRoute} from '@buncha/itemDetails/types/ItemScanner';
import {OrderItemStatus} from '../../../common/types/common';
import PackOrderItem from '../PackOrderItem';
import UnpackItemModal from '../UnpackItemModal';
import listener from '../../../config/listener';
import {navigation} from '@buncha/config/navigation';
import {sortBy} from 'lodash';
import {styles} from './styles';
import {useRoute} from '@react-navigation/native';
import {useUpdateOrderItemStatus} from '../../../batchDetails/hooks/BatchDetails';

interface PackOrderItemListProps {
  packOrderItem: OrderItems[];
  onClick: (
    orderItem: OrderItems,
    status: OrderItemStatus,
    lastItem: boolean,
  ) => void;
  packingFinished: boolean;
  orderLoading: boolean;
  storeIndex: string;
  adjacentShopperDetails?: AdjacentShopperDetails;
  isMeijer: boolean;
}

function PackOrderItemList(props: PackOrderItemListProps) {
  const {packOrderItem, onClick, packingFinished, orderLoading, isMeijer} =
    props;
  const [showModal, setShowModal] = useState(false);
  const [showUnpackModal, setShowUnpackModal] = useState(false);
  const [orderItem, setOrderItem] = useState<OrderItems | null>(null);
  const {updateOrderItemStatus, changeStatusState} = useUpdateOrderItemStatus();
  const route = useRoute();
  const routeParams: ItemScannerRoute | undefined = route?.params;
  const runId = Number(routeParams?.runId);
  const loading = orderLoading || changeStatusState.loading;

  const dropItem = useCallback((itemClicked: OrderItems | null) => {
    setOrderItem(itemClicked);
    setShowModal(true);
  }, []);

  const unpackItem = useCallback((itemClicked: OrderItems | null) => {
    setOrderItem(itemClicked);
    setShowUnpackModal(true);
  }, []);

  const closeUnpackItemModal = useCallback(() => {
    listener.emit('orderPackDropRefresh');
    setShowUnpackModal(false);
    const upcCode = orderItem?.upcCode;
    if (isMeijer && upcCode && (isPlu(upcCode) || isNSC2(upcCode)))
      return navigation.navigate(HomeScreens.B2B, {
        screen: ItemDetailsScreen.NSCItemScanner,
        params: {
          orderItemId: orderItem?.id,
          storeIndex: props?.storeIndex,
          runId,
          catalogItemId: orderItem?.catalogItemId,
          returnPage: ShopperScreens.OrderPackingPage,
        },
      });
    if (orderItem?.upcStatus !== UpcStatus.pending && !isMeijer) return;
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.ItemScanner,
      params: {
        orderItemId: orderItem?.id,
        storeIndex: props?.storeIndex,
        runId,
        quantity:
          orderItem?.shopperItemChangesForShopper?.updatedQuantity ||
          orderItem?.quantity,
        returnPage: ShopperScreens.OrderPackingPage,
        source: 'Packing Screen',
      },
    });
  }, [
    isMeijer,
    orderItem?.catalogItemId,
    orderItem?.id,
    orderItem?.quantity,
    orderItem?.shopperItemChangesForShopper?.updatedQuantity,
    orderItem?.upcCode,
    orderItem?.upcStatus,
    props?.storeIndex,
    runId,
  ]);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);
  const sortedPackOrderItem = sortBy(
    packOrderItem,
    sortedOrderItem => sortedOrderItem.id,
  );

  const changedOrderItemStatus = useCallback(() => {
    if (isMeijer) return closeUnpackItemModal();
    if (runId && orderItem?.id)
      updateOrderItemStatus(
        runId,
        OrderItemStatus.notPacked,
        [orderItem?.id],
        closeUnpackItemModal,
      );
  }, [
    closeUnpackItemModal,
    isMeijer,
    orderItem?.id,
    runId,
    updateOrderItemStatus,
  ]);

  const lastItemIndex = useMemo(() => {
    let lastItem = 0;
    sortedPackOrderItem?.forEach((item, index) => {
      if (item.itemStatus === OrderItemStatus.notPacked) lastItem = index;
    });

    return lastItem;
  }, [sortedPackOrderItem]);

  const renderItem = useCallback(
    ({item, index}: {item: OrderItems; index: number}) => {
      return (
        <PackOrderItem
          runId={runId}
          orderItem={item}
          onClick={(i: OrderItems, status: OrderItemStatus) =>
            onClick(i, status, index === lastItemIndex)
          }
          packingFinished={packingFinished}
          dropItem={dropItem}
          unpackItem={unpackItem}
          adjacentShopperDetails={props?.adjacentShopperDetails}
        />
      );
    },
    [
      dropItem,
      lastItemIndex,
      onClick,
      packingFinished,
      props?.adjacentShopperDetails,
      runId,
      unpackItem,
    ],
  );

  const Separator = useCallback(() => <Divider style={styles.divider} />, []);

  return (
    <Box>
      <CustomFlatList
        data={sortedPackOrderItem}
        renderItem={renderItem}
        ItemSeparatorComponent={Separator}
      />
      <DropItemModalBody
        orderItemId={orderItem?.id || 0}
        onClose={closeModal}
        disableNavigation={true}
        disableState={orderLoading}
        hasListnerReq
        open={showModal}
        isAdjacentOOSItem={orderItem?.isAdjacentOOSItem}
        catalogItemId={orderItem?.catalogItemId}
        orderId={orderItem?.orderId || 0}
      />
      <UnpackItemModal
        onSubmit={changedOrderItemStatus}
        open={showUnpackModal}
        onClose={() => setShowUnpackModal(false)}
        loading={loading}
      />
    </Box>
  );
}

export default React.memo(PackOrderItemList);
